import { useState, useEffect, useCallback, useMemo } from "react";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { es } from "date-fns/locale";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Activity, TrendingUp } from "lucide-react";
import CalendarService from "../../../services/calendar/CalendarService";
import { showError } from "../../../components/alertas";
import logger from "../../../utils/logger";
import "./ingresos.css";

const fmtMoney = (n, digits = 0) => `$${(Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: digits, maximumFractionDigits: 2 })}`;
const longDate = (ymd) => new Date(ymd + "T12:00:00").toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });
// Color de marca para la única serie del gráfico (page.jsx lo pone inline en :root).
const readAccent = () => {
  if (typeof window === "undefined") return "#3b82f6";
  const root = document.documentElement;
  return (root.style.getPropertyValue("--primary-color") || getComputedStyle(root).getPropertyValue("--primary-color")).trim() || "#3b82f6";
};

/**
 * Ingresos: cifras del periodo, barras por semana (una serie, color de marca) y
 * desglose por servicio como lista con barra de proporción. Es una estimación a
 * partir del precio de cada cita agendada, no un registro de cobros.
 */
const IncomesTab = ({ clientData }) => {
  const clientId = clientData?.id;
  const staff = useMemo(() => clientData?.staff || [], [clientData]);
  const [selectedStaffId, setSelectedStaffId] = useState(() => clientData?.staff?.[0]?.id || "");
  const today = useMemo(() => new Date(), []);
  const [startDate, setStartDate] = useState(format(startOfMonth(today), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(endOfMonth(today), "yyyy-MM-dd"));
  const [customDateRange, setCustomDateRange] = useState(false);
  const [dateError, setDateError] = useState("");
  const [incomeData, setIncomeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const accent = useMemo(readAccent, []);

  // clientData puede llegar después del montaje (recarga con ?tab=incomes): elegir
  // al primer profesional en cuanto exista.
  useEffect(() => {
    if (!selectedStaffId && staff.length > 0) setSelectedStaffId(staff[0].id);
  }, [staff, selectedStaffId]);

  const handleDateChange = (type, value) => {
    if (type === "start") {
      const newStart = new Date(value + "T12:00:00");
      const currentEnd = new Date(endDate + "T12:00:00");
      if (newStart > currentEnd) {
        const adjustedEnd = new Date(newStart); adjustedEnd.setDate(adjustedEnd.getDate() + 1);
        setEndDate(format(adjustedEnd, "yyyy-MM-dd"));
        setDateError("La fecha de fin se ajustó para quedar después de la de inicio.");
      } else setDateError("");
      setStartDate(value);
    } else {
      setDateError(new Date(value + "T12:00:00") < new Date(startDate + "T12:00:00") ? "La fecha de fin debe ser posterior a la de inicio." : "");
      setEndDate(value);
    }
  };

  const fetchIncomeData = useCallback(async () => {
    if (!clientId || !selectedStaffId) return;
    setLoading(true);
    try {
      const response = await CalendarService.getIncomeRange(clientId, selectedStaffId, startDate, endDate);
      if (response.data?.success) setIncomeData(response.data.data);
      else showError("Error", "No se pudieron cargar los datos de ingresos");
    } catch (error) {
      logger.error("Error fetching income data:", error);
      showError("Error", "No se pudo obtener los datos de ingresos.");
    } finally {
      setLoading(false);
    }
  }, [clientId, selectedStaffId, startDate, endDate]);

  useEffect(() => { if (clientId && selectedStaffId) fetchIncomeData(); }, [clientId, selectedStaffId, startDate, endDate, fetchIncomeData]);

  const setMonth = (offset) => {
    const d = subMonths(today, offset);
    setStartDate(format(startOfMonth(d), "yyyy-MM-dd"));
    setEndDate(format(endOfMonth(d), "yyyy-MM-dd"));
    setCustomDateRange(false);
    setDateError("");
  };
  const isThisMonth = !customDateRange && startDate === format(startOfMonth(today), "yyyy-MM-dd");
  const isLastMonth = !customDateRange && startDate === format(startOfMonth(subMonths(today, 1)), "yyyy-MM-dd");

  const chartData = useMemo(() => {
    if (!incomeData) return [];
    if (Array.isArray(incomeData.weeklyBreakdown) && incomeData.weeklyBreakdown.length) {
      // "yyyy-MM-dd" a secas se parsea como UTC y en México cae un día antes.
      return incomeData.weeklyBreakdown.map((week) => ({
        name: format(new Date(week.weekStart + "T12:00:00"), "d MMM", { locale: es }),
        label: `${format(new Date(week.weekStart + "T12:00:00"), "d MMM", { locale: es })} – ${format(new Date(week.weekEnd + "T12:00:00"), "d MMM", { locale: es })}`,
        ingresos: week.income || 0,
        citas: week.appointments || 0,
      }));
    }
    return [{ name: "Periodo", label: "Periodo", ingresos: incomeData.totalIncome || 0, citas: incomeData.totalAppointments || 0 }];
  }, [incomeData]);
  const breakdown = useMemo(() => (incomeData?.incomeBreakdown || []).map((s) => ({ name: s.service, value: s.totalAmount || 0, appointments: s.appointments || 0 })).sort((a, b) => b.value - a.value), [incomeData]);
  const maxService = breakdown[0]?.value || 0;
  const hasWeekly = chartData.some((d) => d.ingresos > 0 || d.citas > 0);

  if (!selectedStaffId) {
    return (
      <section className="cp-card">
        <div className="cp-empty">
          <Activity size={40} />
          <h3>{staff.length === 0 ? "Sin profesionales conectados" : "Elige un profesional"}</h3>
          <p>{staff.length === 0 ? "Conecta un profesional con su cuenta de Google en Conexiones para ver ingresos." : "Selecciona de quién quieres ver los números."}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="cp-incomes">
      <header className="cp-page-head">
        <div>
          <h2 className="cp-page-title" data-tour="ingresos.title">Ingresos</h2>
          <p className="cp-page-sub">Estimación a partir del precio de cada cita agendada, no un registro de cobros.</p>
        </div>
      </header>

      <div className="cp-inc-filters" data-tour="ingresos.filters">
        {staff.length > 1 && (
          <div className="cp-segment-scroll">
            <div className="cp-segment" role="tablist" aria-label="Profesional">
              {staff.map((s) => (
                <button key={s.id} type="button" role="tab" aria-selected={selectedStaffId === s.id} className={`cp-segment-btn ${selectedStaffId === s.id ? "is-active" : ""}`} onClick={() => setSelectedStaffId(s.id)}>{s.name || s.nombre}</button>
              ))}
            </div>
          </div>
        )}
        <div className="cp-segment cp-segment-fill cp-inc-period" role="tablist" aria-label="Periodo">
          <button type="button" role="tab" aria-selected={isThisMonth} className={`cp-segment-btn ${isThisMonth ? "is-active" : ""}`} onClick={() => setMonth(0)}>Este mes</button>
          <button type="button" role="tab" aria-selected={isLastMonth} className={`cp-segment-btn ${isLastMonth ? "is-active" : ""}`} onClick={() => setMonth(1)}>Mes anterior</button>
          <button type="button" role="tab" aria-selected={customDateRange} className={`cp-segment-btn ${customDateRange ? "is-active" : ""}`} onClick={() => setCustomDateRange(true)}>Otro</button>
        </div>
        {customDateRange && (
          <div className="cp-inc-dates">
            <label className="cp-field"><span>Desde</span><input type="date" className="cp-input" value={startDate} onChange={(e) => handleDateChange("start", e.target.value)} /></label>
            <label className="cp-field"><span>Hasta</span><input type="date" className="cp-input" value={endDate} onChange={(e) => handleDateChange("end", e.target.value)} /></label>
            {dateError && <small className="cp-inc-err">{dateError}</small>}
          </div>
        )}
        <p className="cp-hint">Del {longDate(startDate)} al {longDate(endDate)}.</p>
      </div>

      {loading && !incomeData ? (
        <div className="cp-stack">
          <div className="cp-inc-kpis">{[0, 1, 2, 3].map((i) => <div key={i} className="cp-skeleton" style={{ height: 88 }} />)}</div>
          <div className="cp-skeleton" style={{ height: 280 }} />
        </div>
      ) : incomeData ? (
        <div className={`cp-stack ${loading ? "is-refreshing" : ""}`}>
          <div className="cp-inc-kpis">
            <div className="cp-inc-kpi is-main">
              <span className="cp-eyebrow">Ingresos</span>
              <span className="cp-inc-kpi-value">{fmtMoney(incomeData.totalIncome)}</span>
              <span className="cp-hint">{incomeData.currency || "MXN"}</span>
            </div>
            <div className="cp-inc-kpi">
              <span className="cp-eyebrow">Citas</span>
              <span className="cp-inc-kpi-value">{incomeData.totalAppointments ?? 0}</span>
              <span className="cp-hint">confirmadas</span>
            </div>
            <div className="cp-inc-kpi">
              <span className="cp-eyebrow">Por cita</span>
              <span className="cp-inc-kpi-value">{fmtMoney(incomeData.averagePerAppointment)}</span>
              <span className="cp-hint">promedio</span>
            </div>
            <div className="cp-inc-kpi">
              <span className="cp-eyebrow">Periodo</span>
              <span className="cp-inc-kpi-value">{incomeData.period?.days || 0}</span>
              <span className="cp-hint">días</span>
            </div>
          </div>

          <article className="cp-card">
            <div className="cp-card-head"><h3 className="cp-card-title"><TrendingUp size={18} /> Por semana</h3></div>
            {hasWeekly ? (
              <div className="cp-inc-chart">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="28%">
                    <CartesianGrid vertical={false} stroke="rgba(15, 23, 42, 0.08)" />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} interval={0} />
                    <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`)} />
                    <Tooltip cursor={{ fill: "rgba(15, 23, 42, 0.04)" }} content={<WeekTooltip />} />
                    <Bar dataKey="ingresos" fill={accent} radius={[4, 4, 0, 0]} maxBarSize={44} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="cp-empty cp-inc-empty">
                <Activity size={32} />
                <h3>Sin ingresos en este periodo</h3>
                <p>Prueba con otras fechas o con otro profesional.</p>
              </div>
            )}
          </article>

          {breakdown.length > 0 && (
            <article className="cp-card">
              <div className="cp-card-head"><h3 className="cp-card-title" data-tour="ingresos.breakdown">Por servicio</h3></div>
              <ul className="cp-inc-services">
                {breakdown.map((s) => (
                  <li key={s.name} className="cp-inc-service">
                    <div className="cp-inc-service-top">
                      <span className="cp-inc-service-name">{s.name}</span>
                      <span className="cp-inc-service-total">{fmtMoney(s.value)}</span>
                    </div>
                    <div className="cp-inc-bar" aria-hidden="true"><span style={{ width: `${maxService ? (s.value / maxService) * 100 : 0}%` }} /></div>
                    <div className="cp-inc-service-sub">{s.appointments} cita{s.appointments === 1 ? "" : "s"} · {fmtMoney(s.value / (s.appointments || 1))} por cita</div>
                  </li>
                ))}
              </ul>
              <div className="cp-inc-total">
                <span>Total</span>
                <span>{breakdown.reduce((a, s) => a + s.appointments, 0)} citas</span>
                <span className="cp-inc-service-total">{fmtMoney(breakdown.reduce((a, s) => a + s.value, 0))}</span>
              </div>
            </article>
          )}
        </div>
      ) : (
        <div className="cp-card"><div className="cp-empty"><Activity size={32} /><h3>Sin datos</h3><p>No hay información para el periodo seleccionado.</p></div></div>
      )}
    </section>
  );
};

function WeekTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="cp-inc-tip">
      <div className="cp-inc-tip-label">{d.label}</div>
      <div><b>{fmtMoney(d.ingresos)}</b> · {d.citas} cita{d.citas === 1 ? "" : "s"}</div>
    </div>
  );
}

export default IncomesTab;
